import { useState, useCallback, useEffect } from 'react';
import {
  ChevronLeft,
  Check,
  Apple,
  MonitorSmartphone,
  Download,
  FolderOpen,
  PanelTop,
  KeyRound,
  Timer,
  Zap,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { Dialog, DialogContent, Button, cn } from '@clarix/ui';

const TOTAL_STEPS = 5;

interface StepDef {
  id: number;
  tag: string;
  title: string;
  subtitle: string;
  icon: typeof Download;
  color: string;
  bgGrad: string;
}

const STEPS: StepDef[] = [
  {
    id: 1,
    tag: 'Platform',
    title: 'Choose your platform',
    subtitle:
      'Clarix Tracker lives in your menu bar so you can track time without switching apps.',
    icon: MonitorSmartphone,
    color: 'text-indigo-600',
    bgGrad: 'from-indigo-500/10 to-violet-500/10',
  },
  {
    id: 2,
    tag: 'Download',
    title: 'Download Clarix Tracker',
    subtitle: 'One click and the installer is yours. It only takes a few seconds.',
    icon: Download,
    color: 'text-blue-600',
    bgGrad: 'from-blue-500/10 to-cyan-500/10',
  },
  {
    id: 3,
    tag: 'Install',
    title: 'Move to Applications',
    subtitle: 'Open the DMG and drag the Clarix icon into your Applications folder.',
    icon: FolderOpen,
    color: 'text-emerald-600',
    bgGrad: 'from-emerald-500/10 to-teal-500/10',
  },
  {
    id: 4,
    tag: 'Sign in',
    title: 'Find it in the menu bar',
    subtitle:
      'Launch Clarix from Applications. It sits in your menu bar, click the icon and sign in.',
    icon: KeyRound,
    color: 'text-amber-600',
    bgGrad: 'from-amber-500/10 to-orange-500/10',
  },
  {
    id: 5,
    tag: 'Ready',
    title: "You're all set",
    subtitle:
      'Start tracking, switch tasks, and stay synced, all from one click in your menu bar.',
    icon: Zap,
    color: 'text-violet-600',
    bgGrad: 'from-violet-500/10 to-fuchsia-500/10',
  },
];

export interface TrackerOnboardingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Direct download URL for the macOS .dmg. The shell reads it from its env
   *  config (VITE_TRACKER_DMG_URL) — packages never touch import.meta.env.
   *  When empty, the download click still advances the flow but opens nothing,
   *  and the final "Download again" link is hidden (clario-frontend parity). */
  dmgUrl?: string;
}

type Platform = 'mac' | 'windows' | 'other';

function detectPlatform(): Platform {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('mac')) return 'mac';
  if (ua.includes('win')) return 'windows';
  return 'other';
}

/**
 * Guided 5-step onboarding for the Clarix Tracker desktop app, opened from
 * the SidebarRail's bottom "Tracker" download button. Exact port of
 * clario-frontend/src/components/layout/TrackerOnboardingModal.tsx.
 */
export function TrackerOnboardingModal({ open, onOpenChange, dmgUrl = '' }: TrackerOnboardingModalProps) {
  const [step, setStep] = useState(1);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [platform] = useState<Platform>(detectPlatform);

  const isMac = platform === 'mac';

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(1);
        setDownloadSuccess(false);
      }, 0);
    }
  }, [open]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleDownloadMac = useCallback(() => {
    setDownloadSuccess(true);
    if (dmgUrl) {
      window.open(dmgUrl, '_blank', 'noopener,noreferrer');
    }
    setTimeout(() => {
      setDownloadSuccess(false);
      setStep(3);
    }, 600);
  }, [dmgUrl]);

  const goBack = () => setStep((s) => Math.max(1, s - 1));
  const goNext = () => {
    if (step === TOTAL_STEPS) {
      handleClose();
      return;
    }
    if (step === 1 && !isMac) return;
    if (step === 2) {
      handleDownloadMac();
      return;
    }
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  // `step` is clamped to [1, TOTAL_STEPS] and STEPS has TOTAL_STEPS entries.
  const current = STEPS[step - 1]!;
  const Icon = current.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-surface w-[95vw] max-w-[520px] gap-0 overflow-visible rounded-2xl border-0 p-0 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.14)]"
        onEscapeKeyDown={() => handleClose()}
      >
        {/* Colored header banner with floating icon */}
        <div
          className={cn('relative rounded-t-2xl bg-gradient-to-br px-4 pb-16 pt-10', current.bgGrad)}
        >
          {/* Decorative orbs — clipped independently */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-t-2xl">
            <div className="bg-surface/20 animate-float-slow absolute -right-8 -top-8 h-32 w-32 rounded-full blur-2xl" />
            <div className="bg-surface/15 animate-float-slow absolute -left-6 bottom-2 h-24 w-24 rounded-full blur-xl [animation-delay:2s]" />
          </div>

          {/* Step tag */}
          <div className="relative z-10 mb-4 flex items-center gap-2">
            <span className="bg-surface inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm font-semibold uppercase tracking-wider text-gray-800 shadow-sm">
              {current.tag}
              <span className="font-normal text-gray-500">
                {step}/{TOTAL_STEPS}
              </span>
            </span>
          </div>

          {/* Progress bar */}
          <div className="relative z-10 mb-6 h-1.5 max-w-[200px] overflow-hidden rounded-full bg-black/10">
            <div
              className="bg-primary h-full rounded-full transition-[width] duration-500 ease-out"
              style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
            />
          </div>

          {/* Title and subtitle */}
          <div key={`head-${step}`} className="animate-content-in relative z-10">
            <h2 className="text-5xl font-bold leading-tight tracking-tight text-gray-900">
              {current.title}
            </h2>
            <p className="mt-2 max-w-[380px] text-xl leading-relaxed text-gray-600">
              {current.subtitle}
            </p>
          </div>

          {/* Floating hero icon */}
          <div key={`icon-${step}`} className="animate-icon-pop absolute -bottom-8 right-8 z-20">
            <div
              className={cn(
                'bg-surface flex h-16 w-16 items-center justify-center rounded-xl',
                'shadow-[0_6px_24px_-4px_rgba(0,0,0,0.15)] ring-1 ring-black/[0.04]',
              )}
            >
              <Icon className={cn('h-7 w-7', current.color)} strokeWidth={1.5} />
            </div>
          </div>
        </div>

        {/* Step body */}
        <div className="px-4 pb-6 pt-12">
          <div key={`body-${step}`} className="animate-content-in min-h-[160px]">
            {step === 1 && <StepPlatform platform={platform} />}
            {step === 2 && (
              <StepDownload
                downloadSuccess={downloadSuccess}
                onDownload={handleDownloadMac}
                onSkip={() => setStep(3)}
              />
            )}
            {step === 3 && <StepInstall />}
            {step === 4 && <StepMenuBar />}
            {step === 5 && <StepDone />}
          </div>
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-between px-4 pb-6 pt-2">
          <Button
            variant="ghost"
            size="xs"
            onClick={goBack}
            className={cn(
              'rounded-lg text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-600 active:scale-95',
              step === 1 && 'invisible',
            )}
          >
            <ChevronLeft className="mr-0.5 h-4 w-4" strokeWidth={1.5} />
            Back
          </Button>
          <div className="flex items-center gap-2">
            {step === TOTAL_STEPS && dmgUrl && (
              <Button
                variant="outline"
                size="xs"
                className="rounded-lg border-gray-200 text-gray-600 transition-all duration-200 hover:bg-gray-50 active:scale-95"
                asChild
              >
                <a href={dmgUrl} download target="_blank" rel="noopener noreferrer">
                  Download again
                </a>
              </Button>
            )}
            <Button
              size="xs"
              onClick={goNext}
              disabled={step === 1 && !isMac}
              className="min-w-[120px] gap-1.5 rounded-lg transition-all duration-200 active:scale-95"
            >
              {step === TOTAL_STEPS ? (
                'Done'
              ) : (
                <>
                  {step === 1 ? 'Get started' : step === 2 ? 'Download & Continue' : 'Continue'}
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                </>
              )}
            </Button>
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Step sub-components ──────────────────────────────────────────── */

function StepPlatform({ platform }: { platform: Platform }) {
  const isMac = platform === 'mac';

  return (
    <div className="space-y-4">
      {/* Auto-detected badge */}
      <div className="flex items-center justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-sm font-medium text-gray-500">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          Detected: {isMac ? 'macOS' : platform === 'windows' ? 'Windows' : 'Unknown'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* macOS card */}
        <button
          type="button"
          className={cn(
            'group relative flex flex-col items-center gap-3 rounded-xl border-2 p-6 transition-all duration-200',
            isMac
              ? 'border-indigo-200 bg-indigo-50/60 hover:scale-[1.02] hover:border-indigo-300 hover:bg-indigo-50 hover:shadow-sm active:scale-[0.98]'
              : 'cursor-not-allowed border-gray-200 bg-gray-50/50 opacity-60',
          )}
          aria-pressed={isMac}
          disabled={!isMac}
        >
          {isMac && (
            <div className="animate-check-bounce absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-500">
              <Check className="h-3 w-3 text-white" strokeWidth={3} />
            </div>
          )}
          <Apple className={cn('h-10 w-10', isMac ? 'text-gray-800' : 'text-gray-400')} strokeWidth={1.5} />
          <div className="text-center">
            <span className={cn('block text-xl font-semibold', isMac ? 'text-gray-900' : 'text-gray-400')}>
              macOS
            </span>
            <span className="mt-0.5 block text-sm text-gray-500">Available now</span>
          </div>
        </button>

        {/* Windows card */}
        <div
          className={cn(
            'relative flex flex-col items-center gap-3 rounded-xl border-2 border-dashed p-6',
            !isMac
              ? 'border-amber-200 bg-amber-50/40'
              : 'cursor-not-allowed border-gray-200 bg-gray-50/50 opacity-60',
          )}
        >
          <MonitorSmartphone
            className={cn('h-10 w-10', !isMac ? 'text-amber-500' : 'text-gray-400')}
            strokeWidth={1.5}
          />
          <div className="text-center">
            <span className={cn('block text-xl font-semibold', !isMac ? 'text-gray-700' : 'text-gray-400')}>
              Windows
            </span>
            <span
              className={cn(
                'mt-0.5 block text-sm',
                !isMac ? 'font-medium text-amber-600' : 'text-gray-400',
              )}
            >
              Coming soon
            </span>
          </div>
        </div>
      </div>

      {/* Windows-detected message */}
      {!isMac && (
        <div className="animate-content-in rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-center">
          <p className="text-lg font-medium text-amber-700">Windows support is coming soon!</p>
          <p className="mt-1 text-sm text-amber-600">
            We&apos;re working hard to bring Clarix Tracker to Windows. Stay tuned for updates.
          </p>
        </div>
      )}
    </div>
  );
}

function StepDownload({
  downloadSuccess,
  onDownload,
  onSkip,
}: {
  downloadSuccess: boolean;
  onDownload: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={onDownload}
        className={cn(
          'flex w-full items-center gap-4 rounded-xl border-2 p-5 transition-all duration-200',
          downloadSuccess
            ? 'border-emerald-200 bg-emerald-50'
            : 'bg-surface border-gray-200 hover:border-indigo-200 hover:bg-indigo-50/30 hover:shadow-sm active:scale-[0.99]',
        )}
      >
        <div
          className={cn(
            'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl transition-colors duration-200',
            downloadSuccess ? 'bg-emerald-100' : 'bg-gray-100',
          )}
        >
          {downloadSuccess ? (
            <Check className="animate-check-bounce h-6 w-6 text-emerald-600" strokeWidth={2} />
          ) : (
            <Download className="h-6 w-6 text-gray-600" strokeWidth={1.5} />
          )}
        </div>
        <div className="flex-1 text-left">
          <span
            className={cn(
              'block text-xl font-medium',
              downloadSuccess ? 'text-emerald-700' : 'text-gray-900',
            )}
          >
            {downloadSuccess ? 'Download started!' : 'Download for Mac'}
          </span>
          <span className="mt-0.5 block text-lg text-gray-500">
            {downloadSuccess ? 'Open the .dmg file when ready' : 'Intel & Apple Silicon supported'}
          </span>
        </div>
        {!downloadSuccess && (
          <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-400" strokeWidth={1.5} />
        )}
      </button>
      <button
        type="button"
        onClick={onSkip}
        className="mx-auto block text-lg text-gray-400 transition-colors hover:text-indigo-500"
      >
        Already installed? Skip ahead
      </button>
    </div>
  );
}

function StepInstall() {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-center gap-4">
        <div className="animate-content-in flex h-16 w-16 items-center justify-center rounded-2xl border border-indigo-100 bg-indigo-50 [animation-delay:0.05s] [animation-fill-mode:backwards]">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-500">
            <span className="text-lg font-bold text-white">C</span>
          </div>
        </div>
        <div className="animate-content-in flex flex-col items-center gap-1 [animation-delay:0.15s] [animation-fill-mode:backwards]">
          <ArrowRight className="h-5 w-5 text-indigo-400" strokeWidth={2} />
          <span className="text-xs font-medium uppercase tracking-wider text-gray-400">drag</span>
        </div>
        <div className="animate-content-in flex h-16 w-16 items-center justify-center rounded-2xl border border-gray-200 bg-gray-100 [animation-delay:0.25s] [animation-fill-mode:backwards]">
          <FolderOpen className="h-7 w-7 text-gray-500" strokeWidth={1.5} />
        </div>
      </div>
      <p className="text-center text-lg leading-relaxed text-gray-500">
        Open the downloaded <strong className="text-gray-700">.dmg</strong> file, then drag the
        Clarix icon to Applications.
      </p>
    </div>
  );
}

function StepMenuBar() {
  return (
    <div className="space-y-5">
      {/* Simulated menu bar */}
      <div className="mx-auto max-w-[320px]">
        <div className="animate-content-in flex h-8 items-center justify-between rounded-lg bg-gray-900 px-3 [animation-delay:0.05s] [animation-fill-mode:backwards]">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full bg-gray-700" />
            <span className="text-xs font-medium text-gray-400">Finder</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-3.5 w-3.5 rounded bg-gray-700" />
            <div className="h-3.5 w-3.5 rounded bg-gray-700" />
            <div className="animate-content-in flex h-4 w-4 items-center justify-center rounded bg-indigo-500 ring-2 ring-indigo-400/40 [animation-delay:0.3s] [animation-fill-mode:backwards]">
              <span className="text-xxs font-bold text-white">C</span>
            </div>
            <div className="h-3.5 w-3.5 rounded bg-gray-700" />
          </div>
        </div>
        <div className="animate-content-in ml-auto mr-6 mt-1 h-3 w-3 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-indigo-400 [animation-delay:0.45s] [animation-fill-mode:backwards]" />
      </div>

      <div className="flex items-center justify-center gap-3">
        <div className="animate-content-in flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 [animation-delay:0.2s] [animation-fill-mode:backwards]">
          <PanelTop className="h-4 w-4 text-indigo-500" strokeWidth={1.5} />
          <span className="text-lg text-gray-600">Click the icon</span>
        </div>
        <ArrowRight className="h-3.5 w-3.5 text-gray-300" strokeWidth={1.5} />
        <div className="animate-content-in flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 [animation-delay:0.35s] [animation-fill-mode:backwards]">
          <KeyRound className="h-4 w-4 text-amber-500" strokeWidth={1.5} />
          <span className="text-lg text-gray-600">Sign in</span>
        </div>
      </div>
    </div>
  );
}

function StepDone() {
  return (
    <div className="space-y-5">
      <div className="animate-confetti-pop flex items-center justify-center gap-4">
        <Sparkles className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 shadow-lg shadow-indigo-200">
          <Check className="h-8 w-8 text-white" strokeWidth={2.5} />
        </div>
        <Sparkles className="h-5 w-5 text-violet-400" strokeWidth={1.5} />
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[
          { icon: Timer, label: 'Track time', color: 'text-blue-500' },
          { icon: Zap, label: 'Switch tasks', color: 'text-amber-500' },
          { icon: Sparkles, label: 'Stay synced', color: 'text-violet-500' },
        ].map((item) => (
          <div
            key={item.label}
            className="flex flex-col items-center gap-1.5 rounded-xl border border-gray-100 bg-gray-50 p-3"
          >
            <item.icon className={cn('h-5 w-5', item.color)} strokeWidth={1.5} />
            <span className="text-sm font-medium text-gray-600">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
